import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { BrowserRouter, Route, Routes } from 'react-router-dom';
import { FormattedMessage } from 'react-intl';
import { useLDClient } from 'launchdarkly-react-client-sdk';
import { Modal, Drawer } from '@mui/material';
import mixpanel from 'mixpanel-browser';
import { useUserContext } from './Providers/UserContext';
import { useSessionsContext } from './Providers/SessionsContext';
import { useModalsContext } from './Providers/ModalsContext';
import { useMixpanelContext } from './Providers/MixpanelContext';
import { useDeviceContext } from './Providers/DeviceContext';
import Sessions from './Components/Sessions/Sessions';
import WelcomeModal from './Components/Modals/WelcomeModal';
import LiveSession from './Components/LiveSession/LiveSession';
import TermsAndConditions from './Components/TermsAndConditions/TermsAndConditions';
import Header from './Components/Header/Header';
import Feedback from './Components/Feedback/FeedbackJune24/FeedbackJune24';
import UserGuidePage from './Components/UserGuide/UserGuidePage';
import ClassGuidePage from './Components/ClassGuide/ClassGuidePage';
import BottomConfirmationPopup from './Components/Shared/BottomConfirmationPopup';
import Filters from './Components/Filters/FiltersMultipleSelect';
import './assets/styles/main.scss';
import OvalLoader from './assets/loader/oval';
import { sendEventToMondly } from './helpers/Event';
import { isTestWebEnv } from './helpers/AuthStrategy';
import CloseTab from './Components/Feedback/CloseTab';
import { getBlockUsersList } from './helpers/Network';
import { getFromLocalStorage, getLangDir, getMobileOperatingSystem } from './helpers/utils';

import closeImage from './assets/image/close-modal-icon.png';

interface AppProps {
  rawLang: string;
  level: number;
  platform: string;
}

function App({ rawLang = 'en', level, platform = 'mobile' }: AppProps) {
  const [lang, setLang] = useState(rawLang);
  const [termsAndConditionsModal, setTermsAndConditionsModal] = useState(false);
  const [isFeedbackScreen, setIsFeedbackScreen] = useState(false);
  const [isUserGuidePage, setIsUserGuidePage] = useState(false);
  const [isClassGuidePage, setIsClassGuidePage] = useState(false);
  const [isShowHeader, setIsShowHeader] = useState(true);
  const [bodyScrollHeight, setBodyScrollHeight] = useState(0);
  const isWelcomeScreenClicked = getFromLocalStorage('isWelcomeScreenClicked');
  const { user, fetchUser, authError } = useUserContext();
  const { mixpanelTrackEvent, mixpanelTimeEvent } = useMixpanelContext();
  const { setPlatform } = useDeviceContext();
  const { modalContent, strictModalContent, updateModalContent, updateStrictModalContent } =
    useModalsContext();
  const { isFiltersDrawerOpen, sessions, updateIsFiltersDrawerOpen, updateFilterQuery } =
    useSessionsContext();
  const [usersBlockList, setBlockUsersList] = useState<string[]>([]);
  const [deviceId, setDeviceId] = useState('');
  const [isShowBottomPopup, setIsShowBottomPopup] = useState(false);
  const [bottomPopupType, setBottomPopupType] = useState('');
  const [bottomPopupTxt, setBottomPopupTxt] = useState('');
  const feedbackQNumber = 4;
  const [yellowProgressBarMaxVal, setYellowProgressBarMaxVal] = useState(feedbackQNumber);
  const [yellowProgressBarCurrVal, setYellowProgressBarCurrVal] = useState(1);
  const isTestWeb = useRef(false);

  // IMPORTANT: set lang from Mondly format to readable locale format:
  useEffect(() => {
    if (lang === 'en_us' || lang === 'en_gb') {
      setLang('en');
    }
  }, [lang]);

  // IMPORTANT: set body height by OS:
  useEffect(() => {
    const os = getMobileOperatingSystem();
    document.documentElement.style.setProperty('--os', os);
  }, []);

  const onShowBottomPopup = (type: string, txt: string) => {
    setBottomPopupType(type);
    setBottomPopupTxt(txt);
    setIsShowBottomPopup(true);
    setTimeout(() => {
      setIsShowBottomPopup(false);
    }, 3000);
  };

  const isCloseTabPage = !!window.location.href.match(/closetab/);

  const getMixPanelDeviceId = useCallback(() => {
    const cookies = document.cookie;
    const cookieArray = cookies.split(';');
    cookieArray.forEach((cookie) => {
      const [name, value] = cookie.trim().split('=');
      if (name.startsWith('mp_')) {
        const jsonValue = JSON.parse(decodeURIComponent(value));
        setDeviceId(jsonValue.$device_id);
      }
    });
    return deviceId;
  }, [deviceId]);

  useEffect(() => {
    mixpanelTimeEvent('AppIsUserId'); // start the timer when page stars loading
    mixpanelTimeEvent('AppGetSessionsFromLaunch'); // start the timer for getSessions time tracking when page stars loading
  }, [mixpanelTimeEvent]);

  // LAUNCHDARKLY:
  const ldClient = useLDClient();
  useEffect(() => {
    ldClient?.waitUntilReady().then(() => {
      if (user?.id) {
        ldClient.identify({ key: user?.id, custom: { language: lang } });
      }
    });
  }, [lang, ldClient, user?.id]);

  useEffect(() => {
    if (process.env.REACT_APP_CONTENT_ENV === 'prod') {
      if (user?.id) {
        mixpanel.identify(user?.id);
        mixpanelTrackEvent('AppIsUserId', {
          userSubscriptions: user?.roles,
          companyId: user?.b2b.companyId,
          groupId: user?.b2b.groupId,
          groupName: user?.b2b.groupName,
          companyName: user?.b2b.companyName,
          userRoles: user?.roles,
        });
      }
    }
  }, [mixpanelTrackEvent, user?.id, user?.roles]);

  useMemo(async () => {
    setBlockUsersList(await getBlockUsersList());
  }, []);

  const handleCloseModal = useCallback(() => {
    updateModalContent(undefined);
  }, [updateModalContent]);

  const handleCloseStrictModal = useCallback(
    (e: any, reason: string) => {
      if (e && reason !== 'backdropClick') {
        updateModalContent(undefined);
        updateStrictModalContent(undefined);
      }
    },
    [updateModalContent, updateStrictModalContent],
  );

  const handleCloseWelcomeModal = useCallback(() => {
    try {
      mixpanelTrackEvent('WelcomeScreenConsent', {
        userId: user?.id,
        companyId: user?.b2b.companyId,
        groupId: user?.b2b.groupId,
        groupName: user?.b2b.groupName,
        companyName: user?.b2b.companyName,
        userRoles: user?.roles,
      });
      if (!isWelcomeScreenClicked) {
        localStorage.setItem('isWelcomeScreenClicked', JSON.stringify(true));
      }
    } catch (err) {
      console.log(err);
    }
    handleCloseModal();
  }, [handleCloseModal, mixpanelTrackEvent, user?.id, isWelcomeScreenClicked]);

  const [isLandscape, setIsLandscape] = useState(window.innerWidth > window.innerHeight);
  useEffect(() => {
    const handleResize = () => {
      setIsLandscape(window.innerWidth > window.innerHeight);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const welcomeScreenModalContent = useMemo(() => {
    return (
      <div className={`modal welcome-modal ${isLandscape ? 'landscape' : ''}`}>
        <button
          type="button"
          className="close-button"
          onClick={handleCloseWelcomeModal}
          aria-label="Close modal"
        >
          <img src={closeImage} alt="close-icon" className="close-icon-img" />
        </button>
        <WelcomeModal />
        <button
          type="submit"
          className="cta-btn mobile-full-width welcome-modal-cta"
          onClick={() => handleCloseWelcomeModal()}
        >
          <FormattedMessage id="welcomeModal.findClass" />
        </button>
      </div>
    );
  }, [handleCloseWelcomeModal, user]);

  const resetKeyboardScroll = () => {
    window.scrollTo(0, 0);
    document.body.scrollTop = 0;
  };

  useEffect(() => {
    // scroll up to avoid ios keyboard push
    if (!modalContent) {
      resetKeyboardScroll();
    }
  }, [modalContent]);

  useEffect(() => {
    if (process.env.REACT_APP_CONTENT_ENV === 'prod') {
      try {
        mixpanel.init(process.env.REACT_APP_PUBLIC_MIXPANEL_CLIENT_SIDE_TOKEN!, {
          debug: false,
        });
      } catch (err) {
        console.log(err);
      }

      try {
        getMixPanelDeviceId();
      } catch (err) {
        console.log(err);
      }
    }
  }, [getMixPanelDeviceId]);

  useEffect(() => {
    if (user) {
      mixpanelTrackEvent('TutoringLaunch', {
        language: lang,
        version: 'v2',
        timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        currTime: new Date().toLocaleTimeString(),
        companyId: user.b2b.companyId,
        groupId: user.b2b.groupId,
        groupName: user.b2b.groupName,
        companyName: user.b2b.companyName,
        userRoles: user.roles,
      });
    }
  }, [user, lang, mixpanelTrackEvent]);

  useEffect(() => {
    try {
      sendEventToMondly(window.mondlyNative?.analyticsTypes.OPEN, {});
    } catch (err) {
      console.log(err);
    }
  }, []);

  useEffect(() => {
    updateFilterQuery({ filterLevel: [], filterTime: [], filterDate: '' });
    setPlatform(platform);
    // IMPORTANT: no dependency in brackets!
  }, []);

  useEffect(() => {
    const urlSearchParams = new URLSearchParams(window.location.search);
    const params = Object.fromEntries(urlSearchParams.entries());
    mixpanelTimeEvent('AppFetchUserDuration');
    // See isTestWebEnv() in AuthStrategy.ts for why the raw env var is used instead
    // of getContentEnv() and why an allowlist is safer than !== 'prod'.
    const isTestWebActive = isTestWebEnv() && urlSearchParams.has('testWeb');
    fetchUser(isTestWebActive, params.hash);
    if (params.hash) setLang('br');
    isTestWeb.current = isTestWebActive;
  }, [mixpanelTimeEvent, fetchUser]);

  useEffect(() => {
    const currentPath = window.location.pathname;
    const regexPattern = /\/sessionend\//;
    const isMatching = regexPattern.test(currentPath);
    if (isMatching) {
      setIsFeedbackScreen(true);
    } else {
      setIsFeedbackScreen(false);
    }
    const LearnerPageRegexPattern = /\/learnerguide\//i;
    const isMatchingLearnerPage = LearnerPageRegexPattern.test(currentPath);
    if (isMatchingLearnerPage) {
      setIsUserGuidePage(true);
    } else {
      setIsUserGuidePage(false);
    }
    const ClassGuidePageRegexPattern = /\/classguide\//i;
    const isMatchingClassGuidePage = ClassGuidePageRegexPattern.test(currentPath);
    if (isMatchingClassGuidePage) {
      setIsClassGuidePage(true);
    } else {
      setIsClassGuidePage(false);
    }
  }, []);

  useEffect(() => {
    try {
      setTimeout(() => {
        if (sessions?.length && !isWelcomeScreenClicked && !user?.isWizard) {
          updateModalContent(welcomeScreenModalContent);
        }
      }, 500);
    } catch (err) {
      console.log(err);
    }
  }, [isWelcomeScreenClicked, sessions?.length, updateModalContent, welcomeScreenModalContent]);

  const bodyRef = useRef<HTMLDivElement>(null);
  const [isSticky, setIsSticky] = useState(false);
  const [lastScrollY, setLastScrollY] = useState(0);

  useEffect(() => {
    const handleScroll = () => {
      if (bodyRef.current) {
        setBodyScrollHeight(bodyRef.current.scrollTop);
        const scrollFromBottom =
          bodyRef.current.scrollHeight - bodyRef.current.clientHeight - bodyRef.current.scrollTop;
        const currentScrollY = bodyRef.current.scrollTop;
        const direction = currentScrollY > lastScrollY ? 'up' : 'down';
        if (direction === 'down' && scrollFromBottom > 100) {
          setIsSticky(true);
        } else if (direction === 'up') {
          setIsSticky(false);
        }
        setLastScrollY(currentScrollY);
      }
    };

    const scrollableElement = bodyRef.current;
    scrollableElement?.addEventListener('scroll', handleScroll);

    return () => {
      scrollableElement?.removeEventListener('scroll', handleScroll);
    };
  }, [lastScrollY]);

  const filtersDrawerSx = {
    '& .MuiPaper-root': {
      marginTop: '52px',
      maxHeight: 'calc(100% - 52px)',
    },
  };

  const onOpenFilters = () => {
    updateIsFiltersDrawerOpen(true);
  };

  const closeFiltersDrawer = () => {
    updateIsFiltersDrawerOpen(false);
    resetKeyboardScroll();
  };

  useMemo(() => {
    if (!isFiltersDrawerOpen) {
      resetKeyboardScroll();
    }
  }, [isFiltersDrawerOpen]);

  useEffect(() => {
    if (platform === 'web' && isUserGuidePage) {
      setIsShowHeader(false);
    } else {
      setIsShowHeader(true);
    }
  }, [platform, isUserGuidePage]);

  useEffect(() => {
    const handleFocus = () => document.body.classList.add('no-scroll');
    const handleBlur = () => document.body.classList.remove('no-scroll');

    window.addEventListener('focusin', handleFocus);
    window.addEventListener('focusout', handleBlur);

    return () => {
      window.removeEventListener('focusin', handleFocus);
      window.removeEventListener('focusout', handleBlur);
    };
  }, []);

  let catchAllContent: React.ReactNode;
  if (authError === 'loop_cap') {
    catchAllContent = (
      <div className="loaderWrapper">
        <p style={{ color: 'white' }}>
          Authentication failed. The service may be temporarily unavailable. Please try again later.
          To retry, click the Logout button.
        </p>
      </div>
    );
  } else if (!user?.id) {
    catchAllContent = (
      <div className="loaderWrapper">
        <OvalLoader />
      </div>
    );
  }

  return (
    <BrowserRouter>
      {isShowHeader && (
        <Header
          bodyScrollHeight={bodyScrollHeight}
          isTermsAndConditions={termsAndConditionsModal}
          lang={lang}
          yellowProgressBarMaxVal={yellowProgressBarMaxVal}
          yellowProgressBarCurrVal={yellowProgressBarCurrVal}
          isFeedbackScreen={isFeedbackScreen}
          setIsFeedbackScreen={setIsFeedbackScreen}
          isUserGuidePage={isUserGuidePage}
          isClassGuidePage={isClassGuidePage}
          platform={platform}
          setIsUserGuidePage={setIsUserGuidePage}
          setIsClassGuidePage={setIsClassGuidePage}
        />
      )}
      <div className={`bodyWrapper ${isFeedbackScreen ? 'feedback' : ''}`} ref={bodyRef}>
        <Routes>
          {/* Callback route must be unconditional — it is reached before the user is
              loaded. The actual exchange is handled in UserContext.fetchUser(); this
              route just renders the loader while that async work completes. */}
          <Route
            path="/api/auth/callback/ies"
            element={
              <div className="loaderWrapper">
                <OvalLoader />
              </div>
            }
          />
          <Route
            path="*"
            element={
              user?.id ? (
                <Routes>
                  <Route path="/closetab" element={<CloseTab />} />
                  <Route
                    path="/"
                    element={
                      <Sessions
                        bodyScrollHeight={bodyScrollHeight}
                        setTermsAndConditionsModal={setTermsAndConditionsModal}
                        lang={lang}
                        level={level}
                        resetKeyboardScroll={resetKeyboardScroll}
                        usersBlockList={usersBlockList}
                        deviceId={deviceId}
                        platform={platform}
                        isSticky={isSticky}
                        setIsUserGuidePage={setIsUserGuidePage}
                        setIsClassGuidePage={setIsClassGuidePage}
                        onOpenFilters={onOpenFilters}
                      />
                    }
                  />
                  <Route
                    path="/session/:sessionId"
                    element={
                      <LiveSession
                        onShowBottomPopup={(type: string, txt: string) =>
                          onShowBottomPopup(type, txt)
                        }
                        usersBlockList={usersBlockList}
                        deviceId={deviceId}
                        lang={lang}
                        platform={platform}
                      />
                    }
                  />
                  <Route path="/block" element={<div> </div>} />
                  <Route
                    path="/sessionend/:sessionId"
                    element={
                      <Feedback
                        resetKeyboardScroll={resetKeyboardScroll}
                        lang={lang}
                        setYellowProgressBarMaxVal={setYellowProgressBarMaxVal}
                        setYellowProgressBarCurrVal={setYellowProgressBarCurrVal}
                        setIsFeedbackScreen={setIsFeedbackScreen}
                      />
                    }
                  />
                  <Route path="/learnerguide" element={<UserGuidePage />} />
                  <Route path="/classguide" element={<ClassGuidePage platform={platform} />} />
                  {/* TODO: merge routes for Sessions */}
                  <Route
                    path="/sessionbook/:sessionbook"
                    element={
                      <Sessions
                        bodyScrollHeight={bodyScrollHeight}
                        setTermsAndConditionsModal={setTermsAndConditionsModal}
                        lang={lang}
                        level={level}
                        resetKeyboardScroll={resetKeyboardScroll}
                        usersBlockList={usersBlockList}
                        deviceId={deviceId}
                        platform={platform}
                        isSticky={isSticky}
                        setIsUserGuidePage={setIsUserGuidePage}
                        setIsClassGuidePage={setIsClassGuidePage}
                        onOpenFilters={onOpenFilters}
                      />
                    }
                  />
                </Routes>
              ) : (
                catchAllContent
              )
            }
          />
        </Routes>
      </div>

      {/* {!modalContent &&
        !isBookedSessions &&
        !isFeedbackScreen &&
        !isCloseTabPage &&
        !isUserGuidePage && (
          <button
            type="submit"
            className={`open-filters-btn ${lang === 'ar' ? 'rtl' : ''}`}
            onClick={() => onOpenFilters()}
          >
            <img src={openFiltersImage} alt="" />
          </button>
        )} */}

      {isCloseTabPage && <CloseTab />}

      {isShowBottomPopup && (
        <BottomConfirmationPopup confirmationType={bottomPopupType} msgTxt={bottomPopupTxt} />
      )}

      {!!modalContent && (
        <Modal
          open={!!modalContent}
          onClose={(event, reason) => {
            if (reason !== 'backdropClick') {
              handleCloseModal();
            }
          }}
          disableAutoFocus
          disableEscapeKeyDown
        >
          <div className="flex justify-center align-center modal-container" dir={getLangDir(lang)}>
            {modalContent}
          </div>
        </Modal>
      )}
      {!!strictModalContent && (
        <Modal
          open={!!strictModalContent}
          onClose={handleCloseStrictModal}
          disableEscapeKeyDown
          disableAutoFocus
        >
          <div className="flex justify-center align-center full modal-container" dir="auto">
            {strictModalContent}
          </div>
        </Modal>
      )}

      {termsAndConditionsModal && (
        <div className="terms-and-conditions-div">
          <Header
            bodyScrollHeight={bodyScrollHeight}
            isTermsAndConditions
            setTermsAndConditionsModal={setTermsAndConditionsModal}
            lang={lang}
            platform={platform}
          />
          <TermsAndConditions />
        </div>
      )}

      <Drawer
        anchor="bottom"
        open={isFiltersDrawerOpen}
        onClose={closeFiltersDrawer}
        className="filters-drawer-container"
        sx={filtersDrawerSx}
      >
        <Filters />
      </Drawer>
    </BrowserRouter>
  );
}

export default App;
